import React, { useEffect } from "react";
import Assistant from "../assistant/Assistant";
import styled from "styled-components";
import { BaseModal } from "./modalStyle";
import ExitButton from "../buttons/ExitButton";
import { useDispatch, useSelector } from "react-redux";
import { loadUsers } from "../user/usersAction";
import { loadProjects } from "../project/projects/projectsAction";
import { FunctionalModal } from "./FunctionalModal";
import { CLEAR_PROJECTS } from "../project/projects/actionTypes";
import { CLEAR_USERS } from "../user/userTypes";
import useSortItems from "../../custom-hooks/useSortItems";
import useTags from "../../custom-hooks/useTags";
import { ErrorMessage, Field, Formik } from "formik";
import * as Yup from "yup";
import { FormFormik, InputTextFormik } from "../inputs/InputStyle";
import SelectUserInputFormik from "../inputs/SelectUserInputFormik";
import SelectProjectInputFormik from "../inputs/SelectProjectInputFormik";
import TagInput from "../inputs/TagInput";
import SelectWeatherInputFormik from "../inputs/SelectWeatherInputFormik";
import SendFormButton from "../buttons/SendFormButton";
import {
  loadAllObjectives,
  patchObjective,
  postObjective,
} from "../objective/ObjectiveActions";
import { loadProject } from "../project/one-project/projectAction";
import StateObjectiveOrIndicatorInput from "../inputs/StateObjectiveOrIndicatorInput";
import { CLEAR_OBJECTIVE } from "../objective/actionTypes";
import TextareaFormikInput from "../inputs/TextareaFormikInput";

const ObjectiveModal = ({
  setOpen,
  selectedObjectiveId = 0,
  projectId = 0,
  pilotId = 0
}) => {
  const { returnAddedItems, returnRemovedItems, sortOneItemTopOfTheList } =
    useSortItems();

  const dispatch = useDispatch();
  const { id: currentUserId } = useSelector((state) => state.currentUser);
  const { users } = useSelector((state) => state.users);
  const { projects: allProjects } = useSelector((state) => state.projects);
  const { name: projectName, comment: projectComment } = useSelector(
    (state) => state.project
  );

  const {
    title,
    comment,
    weather,
    pilot,
    endDate,
    tags,
    onGoing,
    linkedProjects,
  } = useSelector((state) => state.objective);

  const { selectedTags, setSelectedTags, displayTagAccordingToParent } =
    useTags(allProjects, tags);

  useEffect(() => {
    return () => {
      dispatch({ type: CLEAR_PROJECTS });
      dispatch({ type: CLEAR_USERS });
      dispatch({ type: CLEAR_OBJECTIVE });
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadUsers());
    dispatch(loadProjects(currentUserId));
  }, [currentUserId, dispatch]);

  const close = () => {
    setOpen(false);
  };

  const handleSubmit = async (data) => {
    let newData = Object.assign({}, data);
    if (data.projectId !== "") {
      newData.linkedProjects = [{ linkedProjectId: Number(data.projectId) }];
    }
    newData.pilotId = Number(data.pilotId);
    delete newData.projectId;
    if (selectedObjectiveId === 0) {
      let tagsSelected = [];
      selectedTags.forEach((tag) => {
        tagsSelected.push({ tagId: tag.id });
      });
      if (tagsSelected.length > 0) {
        newData.tags = tagsSelected;
      }
      await dispatch(postObjective(newData));
    } else {
      let keyName = "tagId";
      let removedTags = returnRemovedItems(selectedTags, tags, keyName);
      let addedTags = returnAddedItems(selectedTags, tags, keyName);

      if (removedTags) {
        newData.removeTags = removedTags;
      }
      if (addedTags) {
        newData.tags = addedTags;
      }
      await dispatch(patchObjective(selectedObjectiveId, newData));
    }

    if (projectId !== 0) {
      dispatch(loadProject(projectId));
    }

    close();
    dispatch(loadAllObjectives(currentUserId));
  };

  const displayUsers = () => {
    if (selectedObjectiveId !== 0) {
      return sortOneItemTopOfTheList(users, { id: pilot.id });
    } else {
      return users;
    }
  };

  const displayProject = () => {
    const projectSelected = projectId ? { id: projectId } : linkedProjects[0];
    if (selectedObjectiveId !== 0 || projectId) {
      return sortOneItemTopOfTheList(allProjects, projectSelected);
    } else {
      return allProjects;
    }
  };
  const displayProjectName = () => {
    if (selectedObjectiveId !== 0) {
      let project;
      if (linkedProjects.length > 0) {
        project = linkedProjects[0].name;
        return (
          <h3 className={"txt-align w-100"}>
            {" "}
            Objectif relié au projet :{" "}
            <span className={"fw-700"}> {project} </span>
          </h3>
        );
      }
    }
  };

  const handleTagDisplay = (project_id) => {
    displayTagAccordingToParent(project_id);
  };
  const ObjectiveSchema = Yup.object().shape({
    title: Yup.string()
      .max(250, "Le titre de ce résultat clé est trop long")
      .required("Le champs est requis"),
    comment: Yup.string().max(1000, "Ce commentaire est trop long").nullable(),
    weather: Yup.string().required("Le champs météo est requis"),
    endDate: Yup.string().nullable(),
    onGoing: Yup.boolean().required("Le champs est requis"),
    pilotId: Yup.string().required("Il est obligatoire de choisir un pilote"),
    projectId: Yup.string(),
  });
  return (
    <FunctionalModal
      setOpen={setOpen}
      component={
        <Modal>
          <Formik
            enableReinitialize
            initialValues={{
              title: selectedObjectiveId !== 0 ? title : "",
              comment: selectedObjectiveId !== 0 ? comment : "",
              endDate: selectedObjectiveId !== 0 ? endDate : "",
              weather: selectedObjectiveId !== 0 ? weather : null,
              pilotId: selectedObjectiveId !== 0 ? pilot ? pilot.id : "" : pilotId ,
              onGoing: selectedObjectiveId !== 0 ? onGoing : true,
              projectId: projectId
                ? projectId
                : linkedProjects.length > 0
                ? linkedProjects[0].id
                : "",
            }}
            validationSchema={ObjectiveSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, isSubmitting, values, setFieldValue }) => (
              <>
                <FirstSection>
                  <h1>
                    {" "}
                    {selectedObjectiveId ? "Modifier" : "Ajouter"} un objectif{" "}
                  </h1>
                  <ExitButton closeUpdate={close} />
                </FirstSection>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Assistant
                    context={{
                      object: "objective",
                      project: projectName,
                      comment: projectComment,
                    }}
                    onSelect={(item) => {
                      setFieldValue("title", item.name);
                      setFieldValue("comment", item.description);
                    }}
                  />
                </div>
                <Form>
                  {displayProjectName()}
                  <SendFormButton
                      state={isValid && !isSubmitting}
                      actionName={ObjectiveSchema ? "Enregistrer" : "Ajouter"}
                  />
                  <div>
                    <label htmlFor="title"> Nom de l'objectif * </label>
                    <InputTextFormik
                      name="title"
                      placeholder={"Nom de l'objectif"}
                    />
                    <ErrorMessage
                      name="title"
                      render={(msg) => (
                        <div>
                          <p className="error-text"> {msg} </p>
                        </div>
                      )}
                    />
                  </div>
                  <CommentSection>
                    <label htmlFor="comment"> Commentaires </label>
                    <TextareaFormikInput
                      name="comment"
                      rows="4"
                      placeholder="Vos commentaires ici"
                    />
                    <ErrorMessage
                      name="comment"
                      render={(msg) => (
                        <div>
                          <p className="error-text"> {msg} </p>
                        </div>
                      )}
                    />
                  </CommentSection>
                  <SelectUserInputFormik
                    pilotId={ values.pilotId ? values.pilotId : pilotId }
                    users={displayUsers()}
                    label={"responsable"}
                  />
                  {!projectId && (
                    <SelectProjectInputFormik
                      projects={displayProject()}
                      projectId={values.projectId}
                      handleTagDisplay={handleTagDisplay}
                    />
                  )}
                  <TagInput
                    isLimitedItem={values.projectId !== ""}
                    setSelectedTags={setSelectedTags}
                    selectedTags={selectedTags}
                    actionName={"objectives"}
                  />
                  <StateContainer>
                    <label htmlFor="onGoing"> Statut </label>
                    <StateObjectiveOrIndicatorInput value={values.onGoing} />
                  </StateContainer>
                  <hr />
                  <Deadline>
                    <label htmlFor="endDate"> Échéance </label>
                    <Field name="endDate" type={"date"} max={linkedProjects[0]?.endDate}/>
                    <ErrorMessage
                      name="endDate"
                      render={(msg) => (
                        <div>
                          <p className="error-text"> {msg} </p>
                        </div>
                      )}
                    />
                  </Deadline>

                  <Weathers>
                    <label> Météo * </label>{" "}
                    <SelectWeatherInputFormik value={values.weather} />
                  </Weathers>
                </Form>
              </>
            )}
          </Formik>
        </Modal>
      }
    />
  );
};

const StateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;
const Deadline = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  label {
    white-space: nowrap;
  }
`;
const Modal = styled(BaseModal)`
  align-items: center;
  width: 100vw;
  height: fit-content;
  gap: 1em;
  position: unset;

  @media (min-width: 768px) {
    width: 650px;
  }
`;

const FirstSection = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  h1 {
    flex-grow: 1;
  }

  button {
    display: flex;
    align-items: flex-start;
    height: 100%;
  }
`;

const CommentSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const Form = styled(FormFormik)`
  display: flex;
  flex-direction: column;
  gap: 1em;
  max-width: 90%;
`;

const Weathers = styled.div`
  border-radius: 10px;
  border: 1px solid var(--color-purple-lighten);
  padding-top: 0.5rem;

  label {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export default ObjectiveModal;
